import { FC } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import {
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import ControlledDateField from "../../components/controlledComponents/ControlledDateField";
import { CONSTANTS } from "../../../utils/CONSTS";
import { pages } from "../../../utils/common";
import { logger } from "../../../utils/common";
import { useOnboardInstituteHook } from "../../../hooks/useInstituteHook";
import {
  INSTITUTE_CREATE_STATUSES,
  TInstituteType,
} from "../../../apis/institute/institute.interfaces";

const instituteTypes: TInstituteType[] = [
  "CBSE",
  "ICSE",
  "State Board",
  "IB",
  "IGCSE",
  "Other",
];

const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const createInstituteSchema = z.object({
  // Institute fields
  name: z
    .string()
    .min(2, "Institute name must be at least 2 characters")
    .max(100, "Institute name must not exceed 100 characters"),
  type: z.enum(["CBSE", "ICSE", "State Board", "IB", "IGCSE", "Other"], {
    errorMap: () => ({ message: "Please select a valid institute type" }),
  }),
  registrationNumber: z
    .string()
    .min(3, "Registration number must be at least 3 characters")
    .max(50, "Registration number must not exceed 50 characters"),
  registrationDate: z
    .any()
    .refine((val) => val !== null && val !== undefined, {
      message: "Registration date is required",
    })
    .refine((val) => {
      if (!val) return false;
      const date = dayjs.isDayjs(val) ? val : dayjs(val);
      return date.isValid();
    }, "Please enter a valid date"),
  legalEntity: z
    .string()
    .min(2, "Legal entity must be at least 2 characters")
    .max(200, "Legal entity must not exceed 200 characters"),
  status: z.enum(["draft", "pending", "active", "other"], {
    errorMap: () => ({ message: "Please select a valid status" }),
  }),
  domainSlug: z
    .string()
    .min(2, "Domain slug must be at least 2 characters")
    .max(63, "Domain slug must not exceed 63 characters")
    .regex(slugRegex, "Only lowercase letters, digits and hyphens; must not start or end with a hyphen"),

  // Admin user fields
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  gender: z.enum(["male", "female", "trans"], {
    errorMap: () => ({ message: "Please select a gender" }),
  }),
  userName: z
    .string()
    .min(3, "Username must be at least 3 characters")
    .max(50, "Username must not exceed 50 characters"),
  userPassword: z
    .string()
    .min(6, "Password must be at least 6 characters"),
  designation: z.string().min(1, "Designation is required"),
  email: z.string().email("Please enter a valid email"),
  countryCode: z.string().min(1, "Country code is required"),
  phoneNumber: z.string().min(5, "Phone number is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  country: z.string().min(1, "Country is required"),
  pinCode: z.string().min(1, "Pin code is required"),
});

type CreateInstituteFormData = z.infer<typeof createInstituteSchema>;

const CreateInstituteView: FC = () => {
  const navigate = useNavigate();
  const onboardMutation = useOnboardInstituteHook();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<CreateInstituteFormData>({
    resolver: zodResolver(createInstituteSchema),
    defaultValues: {
      name: "",
      type: "CBSE",
      registrationNumber: "",
      registrationDate: null,
      legalEntity: "",
      status: "draft",
      domainSlug: "",
      firstName: "",
      lastName: "",
      gender: "male",
      userName: "",
      userPassword: "",
      designation: "",
      email: "",
      countryCode: "+91",
      phoneNumber: "",
      city: "",
      state: "",
      country: "India",
      pinCode: "",
    },
  });

  const onSubmit = async (data: CreateInstituteFormData) => {
    try {
      const registrationDate = dayjs.isDayjs(data.registrationDate)
        ? data.registrationDate
        : dayjs(data.registrationDate);

      const result = await onboardMutation.mutateAsync({
        name: data.name,
        type: data.type,
        registrationNumber: data.registrationNumber,
        registrationDate: registrationDate.toISOString(),
        legalEntity: data.legalEntity,
        status: data.status,
        domainSlug: data.domainSlug,
        admin: {
          firstName: data.firstName,
          lastName: data.lastName,
          gender: data.gender,
          userName: data.userName,
          userPassword: data.userPassword,
          designation: data.designation,
          email: data.email,
          countryCode: data.countryCode,
          phoneNumber: data.phoneNumber,
          city: data.city,
          state: data.state,
          country: data.country,
          pinCode: data.pinCode,
        },
      });

      logger.show_success("Institute onboarded successfully!");
      navigate(`${pages.institute.path}/${result.instituteId}`);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to create institute. Please try again.",
      );
    }
  };

  const instituteTypeOptions = instituteTypes.map((type) => ({
    key: type,
    value: type,
  }));

  const instituteStatusOptions = INSTITUTE_CREATE_STATUSES.map((s) => ({
    key: s,
    value: s.charAt(0).toUpperCase() + s.slice(1),
  }));

  const genderOptions = [
    { key: "male", value: "Male" },
    { key: "female", value: "Female" },
    { key: "trans", value: "Trans" },
  ];

  const isPending = isSubmitting || onboardMutation.isPending;

  return (
    <>
      <DocumentTitle title={`${CONSTANTS.title.institute} - Create`} />

      <Grid container spacing={3} data-testid="create-institute-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="create-institute-header"
          >
            <MainTitle title="Create New Institute" />
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate(pages.institute.path)}
              data-testid="back-to-list-button"
            >
              Back to List
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="create-institute-form-card">
            <CardContent>
              {onboardMutation.isError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {onboardMutation.error?.message ||
                    "Failed to create institute. Please try again."}
                </Alert>
              )}

              <form
                onSubmit={handleSubmit(onSubmit)}
                data-testid="create-institute-form"
              >
                <Grid container spacing={3}>
                  {/* ── Institute details ── */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Institute Details
                    </Typography>
                  </Grid>

                  <ControlledTextField
                    control={control as any}
                    name="name"
                    id="institute-name"
                    label="Institute Name"
                    required
                  />

                  <ControlledSelectField
                    control={control as any}
                    name="type"
                    id="institute-type"
                    label="Institute Type"
                    itemList={instituteTypeOptions}
                    isRequired
                  />

                  <ControlledSelectField
                    control={control as any}
                    name="status"
                    id="institute-status"
                    label="Status"
                    itemList={instituteStatusOptions}
                    isRequired
                  />

                  <ControlledTextField
                    control={control as any}
                    name="registrationNumber"
                    id="registration-number"
                    label="Registration Number"
                    required
                  />

                  <ControlledDateField
                    control={control as any}
                    name="registrationDate"
                    id="registration-date"
                    label="Registration Date"
                    required
                  />

                  <ControlledTextField
                    control={control as any}
                    name="legalEntity"
                    id="legal-entity"
                    label="Legal Entity Information"
                    required
                    multiline
                    rows={3}
                  />

                  <ControlledTextField
                    control={control as any}
                    name="domainSlug"
                    id="domain-slug"
                    label="Domain Slug (e.g. greenwood-high)"
                    required
                  />

                  {/* ── Admin user ── */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Institute Admin Account
                    </Typography>
                  </Grid>

                  <ControlledTextField
                    control={control as any}
                    name="firstName"
                    id="admin-first-name"
                    label="First Name"
                    required
                  />

                  <ControlledTextField
                    control={control as any}
                    name="lastName"
                    id="admin-last-name"
                    label="Last Name"
                    required
                  />

                  <ControlledSelectField
                    control={control as any}
                    name="gender"
                    id="admin-gender"
                    label="Gender"
                    itemList={genderOptions}
                    isRequired
                  />

                  <ControlledTextField
                    control={control as any}
                    name="designation"
                    id="admin-designation"
                    label="Designation"
                    required
                  />

                  <ControlledTextField
                    control={control as any}
                    name="userName"
                    id="admin-username"
                    label="Username"
                    required
                  />

                  <ControlledTextField
                    control={control as any}
                    name="userPassword"
                    id="admin-password"
                    label="Password"
                    required
                    type="password"
                  />

                  <ControlledTextField
                    control={control as any}
                    name="email"
                    id="admin-email"
                    label="Email"
                    required
                    type="email"
                  />

                  <ControlledTextField
                    control={control as any}
                    name="countryCode"
                    id="admin-country-code"
                    label="Country Code"
                    required
                  />

                  <ControlledTextField
                    control={control as any}
                    name="phoneNumber"
                    id="admin-phone"
                    label="Phone Number"
                    required
                  />

                  <ControlledTextField
                    control={control as any}
                    name="city"
                    id="admin-city"
                    label="City"
                    required
                  />

                  <ControlledTextField
                    control={control as any}
                    name="state"
                    id="admin-state"
                    label="State"
                    required
                  />

                  <ControlledTextField
                    control={control as any}
                    name="country"
                    id="admin-country"
                    label="Country"
                    required
                  />

                  <ControlledTextField
                    control={control as any}
                    name="pinCode"
                    id="admin-pin-code"
                    label="Pin Code"
                    required
                  />

                  {/* ── Actions ── */}
                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      gap={2}
                      justifyContent="flex-end"
                      sx={{ mt: 2 }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => navigate(pages.institute.path)}
                        disabled={isPending}
                        data-testid="cancel-create-institute-button"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={
                          isPending ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Save />
                          )
                        }
                        disabled={isPending}
                        data-testid="create-institute-submit-button"
                      >
                        {isPending ? "Creating..." : "Create Institute"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default CreateInstituteView;
